var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef } from 'react';
import { ExpandLess, FilterList, HighlightOffOutlined } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { useIsMobileResolution } from 'root/src/client/v2/browser/hooks/useIsMobileResolution';
import { createPortal } from 'react-dom';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import { useDaresFilter } from 'root/src/client/web/record/DropEvent/DaresFilter/DaresFilterProvider';
import { PlatformFilter } from 'root/src/client/web/record/DropEvent/DaresFilter/PlatformFilter';
import { BattlePassTierFilter } from 'root/src/client/web/record/DropEvent/DaresFilter/BattlePassTierFilter';
import { useOnClickOutside } from 'usehooks-ts';
import { BattlePassFilter } from 'root/src/client/web/record/DropEvent/DaresFilter/BattlePassFilter';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { DROPS_ANALYTIC_EVENTS } from 'root/src/shared/drops/drops.analytics';
import { LevelFilter } from './LevelFilter';
export const DaresFilters = React.memo(() => {
    const ref = useRef(null);
    const { isOpen } = useDaresFilter();
    return (<div ref={ref} className={twMerge('flex absolute w-24 flex-initial items-start justify-start z-10', isOpen && 'static w-96')}>
			<div className="flex flex-initial relative">
				<AnimatePresence>
					{isOpen && (<FiltersContent />)}
				</AnimatePresence>
				<TriggerButton />
			</div>
		</div>);
});
export const FiltersContent = React.memo(() => {
    const { isMobileResolution } = useIsMobileResolution();
    const ref = useRef(null);
    const { setIsOpen } = useDaresFilter();
    useOnClickOutside(ref, () => {
        setIsOpen(false);
    });
    if (isMobileResolution) {
        return (<>
				{createPortal(<div ref={ref} className="fixed left-0 top-0 h-[100vh] z-[1000] animate-pop-up w-full overflow-hidden py-4 bg-black flex flex-col px-6">
						<CloseButton />
						<FiltersHeader />
						<SelectedFilters />
						<Filters />
						<ApplyButton />
					</div>, document.body)}
			</>);
    }
    return (<motion.div ref={ref} initial={{
            width: 0,
            opacity: 0,
            scale: 0,
        }} animate={{
            width: isMobileResolution ? '100%' : 400,
            opacity: 1,
            scale: 1,
        }} exit={{
            width: 0,
            opacity: 0,
            scale: 0,
        }}>
			<div className="py-4 bg-black drop-shadow-xl shadow-xl flex flex-col px-6">
				<FiltersHeader />
				<SelectedFilters />
				<Filters />
			</div>
		</motion.div>);
});
const SelectedFilter = React.memo((_a) => {
    var { filter } = _a, props = __rest(_a, ["filter"]);
    const { state, options, onFilterChange } = useDaresFilter();
    const allOptions = React.useMemo(() => {
        return [...options.platforms, ...options.levels, ...options.tiers, ...options.battlePasses];
    }, [options]);
    const translatedFilter = React.useMemo(() => {
        var _a;
        return (_a = allOptions.find(({ value }) => value === filter)) === null || _a === void 0 ? void 0 : _a.label;
    }, [filter, allOptions]);
    const handleRemoveFilter = () => {
        const platforms = state.platforms.filter(item => item !== filter);
        const levels = state.levels.filter(item => item !== filter);
        const tiers = state.tiers.filter(item => item !== filter);
        const battlePasses = state.battlePasses.filter(item => item !== filter);
        onFilterChange(Object.assign(Object.assign({}, state), { platforms,
            levels,
            tiers,
            battlePasses }));
    };
    return (<div {...props} className={twMerge('bg-primary p-2 sm:p-0 sm:pl-2 sm:pr-1 rounded-2xl sm:py-0.5 text-sm flex justify-center items-center gap-0.5', props.className)}>
			{translatedFilter}
			<button onClick={handleRemoveFilter} type="button">
				<HighlightOffOutlined fontSize="medium"/>
			</button>
		</div>);
});
const FiltersSpacer = React.memo(() => {
    return (<div className="w-full my-2 h-[0.5px] bg-white"/>);
});
const FiltersHeader = React.memo(() => {
    const { drop } = useDaresFilter();
    return (<div className="flex flex-initial flex-col gap-0.5 py-1">
			<p className="capitalize">Filter dares:</p>
			<span className="font-mono text-light-4">All Dares for {drop.campaignName}</span>
			<FiltersSpacer />
		</div>);
});
const SelectedFilters = React.memo(() => {
    const { state, resetFilters } = useDaresFilter();
    const selectedFilters = React.useMemo(() => {
        return [...state.platforms, ...state.levels, ...state.tiers, ...state.battlePasses];
    }, [state]);
    const handleClearFilters = () => {
        resetFilters();
    };
    if (!selectedFilters.length) {
        return null;
    }
    return (<div className="flex w-full py-2 gap-3 flex-initial">
			<ul className="flex w-full gap-1.5 flex-initial flex-wrap">
				{selectedFilters.map(item => (<li className="animate-pop-up" key={item}>
						<SelectedFilter filter={item}/>
					</li>))}
			</ul>
			<button type="button" className="flex justify-center items-center flex-initial text-pink text-xs gap-0.5 whitespace-nowrap" onClick={handleClearFilters}>
				<HighlightOffOutlined className="*:fill-pink" fontSize="small"/>
				Clear All
			</button>
		</div>);
});
export const TriggerButton = React.memo(() => {
    const { isOpen, setIsOpen } = useDaresFilter();
    const handleTriggerClick = () => {
        analyticsSendItem(DROPS_ANALYTIC_EVENTS.DROP_DARE_FILTERS, 'open_filters_window');
        setIsOpen(!isOpen);
    };
    return (<button onClick={handleTriggerClick} type="button" className={twMerge('shadow-xl drop-shadow-xl group origin-left rotate-90 translate-x-6 -translate-y-6 relative grid uppercase grid-cols-3 w-32 h-12 px-4 py-2 bg-light-3 justify-center items-center flex-initial rounded-tr rounded-tl', !isOpen && 'rounded-br rounded-bl')}>
			<div className="w-6 h-6 absolute flex justify-center items-center bg-light-3 rounded-full right-1/2 z-10 translate-x-1/2 -top-2">
				<ExpandLess className={twMerge('mb-2 *:fill-dark-2', isOpen && 'rotate-180')}/>
			</div>
			<FilterList className="*:fill-dark-2"/>
			<span className="col-span-2 text-dark-2 text-left z-10">Filters</span>
		</button>);
});
export const Filters = React.memo(() => {
    return (<div className="py-2 flex flex-col overflow-y-auto px-1 py-4">
			<LevelFilter />
			<PlatformFilter />
			<BattlePassTierFilter />
			<BattlePassFilter />
		</div>);
});
const CloseButton = React.memo(() => {
    const { setIsOpen } = useDaresFilter();
    return (<button type="button" className="flex justify-center items-center flex-initial text-pink text-xs gap-0.5 self-end" onClick={() => setIsOpen(false)}>
			<HighlightOffOutlined className="*:fill-pink" fontSize="large"/>
		</button>);
});
const ApplyButton = React.memo(() => {
    const { setIsOpen } = useDaresFilter();
    return (<ButtonNew className="mb-2 py-3 text-xl" type="button" onClick={() => setIsOpen(false)}>
			Apply
		</ButtonNew>);
});
